/**
 * Editing a patient note from a preview surface (Treatment Record, timeline).
 *
 * Pure helpers only — the SQL lives in the app's note service. They exist because
 * one class of `patient_notes` row is NOT free-standing: Billing mirrors an invoice's
 * `notes` field into a note with a deterministic id so it shows up on the Treatment
 * Record and the Patient Timeline. Editing that mirror as if it were an ordinary note
 * would desynchronise it — the invoice would keep the old text, and the next invoice
 * save would silently overwrite the edit.
 *
 * So an editor has to be able to recognise a mirror and write through to its source.
 */

/** Prefix Billing uses when mirroring `invoices.notes` into `patient_notes`. */
export const INVOICE_MIRROR_NOTE_PREFIX = 'invoice-note:';

/** Deterministic id of the note mirroring an invoice's notes field. */
export function invoiceMirrorNoteId(invoiceId: string): string {
  return `${INVOICE_MIRROR_NOTE_PREFIX}${invoiceId}`;
}

/**
 * The invoice a note mirrors, or null for an ordinary note.
 *
 * Only a non-empty invoice id counts: a bare `"invoice-note:"` is malformed and must
 * not be treated as a mirror, or a write-through would target no invoice at all.
 */
export function invoiceIdFromMirrorNote(noteId: string | null | undefined): string | null {
  if (!noteId || !noteId.startsWith(INVOICE_MIRROR_NOTE_PREFIX)) return null;
  const invoiceId = noteId.slice(INVOICE_MIRROR_NOTE_PREFIX.length);
  return invoiceId.length > 0 ? invoiceId : null;
}

/** True when editing this note must also write through to its invoice. */
export function isInvoiceMirrorNote(noteId: string | null | undefined): boolean {
  return invoiceIdFromMirrorNote(noteId) !== null;
}

export interface NoteBodyValidation {
  ok: boolean;
  /** The text to persist — trimmed. Only meaningful when `ok`. */
  value: string;
  error?: string;
}

/**
 * A note must have something in it. An all-whitespace body is a delete in disguise,
 * and saving it would leave an empty bullet in the Treatment Record with no way to
 * tell it apart from a rendering bug — so it is refused and the user is pointed at
 * Delete instead.
 */
export function validateNoteBody(input: string): NoteBodyValidation {
  const value = input.trim();
  if (!value) {
    return { ok: false, value: '', error: 'A note cannot be empty. Use Delete to remove it.' };
  }
  return { ok: true, value };
}

/** True when the text differs from what is stored (ignoring surrounding whitespace). */
export function noteBodyChanged(original: string | null | undefined, next: string): boolean {
  return (original ?? '').trim() !== next.trim();
}
